import { FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { IconEye, IconEyeOff } from '@tabler/icons-react';
import { isAuthConfigured, useAuth0 } from '../auth/AuthProvider';
import { consumeLoginEmailPrefill } from '../utils/magicLink';

type AuthView = 'login' | 'create' | 'forgot';

const fieldClassName =
  'h-[50px] w-full rounded-[13px] border border-white/[0.18] bg-white/[0.06] px-4 font-manrope text-[15px] text-white outline-none placeholder:text-white/40 transition focus:border-[#5FD6E0] focus:bg-white/[0.09] focus:shadow-[0_0_0_4px_rgba(95,214,224,0.16)]';

const ctaClassName =
  'mt-1.5 h-[50px] w-full rounded-[13px] bg-gradient-to-br from-[#F06E57] to-[#E45B47] font-manrope text-[15px] font-bold tracking-[0.2px] text-white shadow-[0_12px_28px_rgba(228,91,71,0.35)] transition hover:-translate-y-px hover:shadow-[0_14px_34px_rgba(228,91,71,0.5)] active:translate-y-0 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0';

const Login = () => {
  const {
    isAuthenticated,
    isLoading,
    loginWithPassword,
    signUpWithPassword,
    requestPasswordReset,
  } = useAuth0();
  const navigate = useNavigate();
  const videoRef = useRef<HTMLVideoElement>(null);

  const [view, setView] = useState<AuthView>('login');
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState(() => consumeLoginEmailPrefill());
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const redirectAfterLogin = useMemo(
    () => localStorage.getItem('redirectAfterLogin') || '/',
    [],
  );

  useEffect(() => {
    if (!isAuthConfigured || isLoading || !isAuthenticated) {
      return;
    }

    const returnTo = localStorage.getItem('redirectAfterLogin') || '/';
    localStorage.removeItem('redirectAfterLogin');
    navigate(returnTo, { replace: true });
  }, [isAuthenticated, isLoading, navigate]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) {
      return;
    }

    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const syncMotionPreference = () => {
      if (mediaQuery.matches) {
        video.pause();
        return;
      }
      void video.play().catch(() => undefined);
    };

    syncMotionPreference();
    mediaQuery.addEventListener('change', syncMotionPreference);
    return () => mediaQuery.removeEventListener('change', syncMotionPreference);
  }, []);

  const clearFeedback = () => {
    setErrorMessage('');
    setSuccessMessage('');
  };

  const handleSwitchView = (nextView: AuthView) => {
    setView(nextView);
    clearFeedback();
    setPassword('');
    setShowPassword(false);
  };

  const handleAuthenticatedRedirect = () => {
    const returnTo = localStorage.getItem('redirectAfterLogin') || '/';
    localStorage.removeItem('redirectAfterLogin');
    navigate(returnTo, { replace: true });
  };

  const handlePasswordSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setIsSubmitting(true);
    clearFeedback();

    try {
      await loginWithPassword(email.trim(), password);
      handleAuthenticatedRedirect();
    } catch (error) {
      setErrorMessage(
        error instanceof Error
          ? error.message
          : 'Sign in failed. Please try again.',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreateSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setIsSubmitting(true);
    clearFeedback();

    try {
      await signUpWithPassword(email.trim(), password, fullName.trim());
      handleAuthenticatedRedirect();
    } catch (error) {
      setErrorMessage(
        error instanceof Error
          ? error.message
          : 'Could not create account. Please try again.',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleForgotSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setIsSubmitting(true);
    clearFeedback();

    try {
      await requestPasswordReset(email.trim());
      setSuccessMessage(
        'If an account exists for that email, a reset link is on the way.',
      );
    } catch (error) {
      setErrorMessage(
        error instanceof Error
          ? error.message
          : 'Could not send reset link. Please try again.',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isAuthConfigured) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#04191D] px-4 font-manrope text-[#EAF6F6]">
        <div className="max-w-md text-center">
          <h1 className="mb-2 font-dm-serif text-3xl italic text-white">
            App update required
          </h1>
          <p className="mb-4 text-[#B9D3D5]">
            Your browser is using an outdated version of the app. Clear your
            Safari cache for santtai.com or open the site in a private window,
            then try again.
          </p>
          <Link
            to="/"
            className="font-semibold text-[#5FD6E0] underline hover:text-[#8CE6EE]"
          >
            Return to home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="relative flex min-h-screen w-full items-center justify-center overflow-hidden bg-[#04191D] font-manrope text-[#EAF6F6]">
      <video
        ref={videoRef}
        className="absolute inset-0 z-0 h-full w-full object-cover"
        autoPlay
        loop
        muted
        playsInline
        preload="metadata"
        aria-hidden="true"
      >
        <source src="/videos/login-reef.mp4" type="video/mp4" />
      </video>
      <div className="absolute inset-0 z-[1] bg-gradient-to-b from-[rgba(4,25,29,0.35)] to-[rgba(4,25,29,0.8)]" />

      <Link
        to="/"
        className="absolute left-1/2 top-10 z-[2] -translate-x-1/2 rounded-full bg-white/90 px-4 py-2 shadow-[0_8px_24px_rgba(0,0,0,0.25)] backdrop-blur-sm"
        aria-label="Santtai home"
      >
        <img
          src="https://imagedelivery.net/KQLaZdBjDzxarhwBRuqRFg/33a0d3dd-4713-44fc-2792-1237db489400/public"
          alt="Santtai"
          className="h-8 w-auto"
          width={150}
          height={32}
        />
      </Link>

      <div className="relative z-[2] mx-6 mb-10 mt-[100px] flex w-full max-w-[440px] animate-login-fade-up flex-col gap-7 rounded-3xl border border-white/[0.14] bg-[rgba(10,40,45,0.55)] px-10 py-11 shadow-[0_30px_80px_rgba(0,0,0,0.45)] backdrop-blur-[22px]">
        {view === 'login' ? (
          <>
            <div className="flex flex-col gap-2.5 text-center">
              <span className="text-xs font-bold uppercase tracking-[2px] text-[#5FD6E0]">
                Dive deeper
              </span>
              <h1 className="font-dm-serif text-[38px] italic leading-tight text-white">
                Welcome back to the reef
              </h1>
              <p className="text-[14.5px] leading-[22px] text-[#B9D3D5]">
                Sign in to manage your bookings and saved trips.
              </p>
            </div>

            <form
              className="flex flex-col gap-3.5"
              onSubmit={handlePasswordSubmit}
            >
              <label className="flex flex-col gap-1.5 text-left text-[13px] font-semibold text-[#B9D3D5]">
                Email
                <input
                  type="email"
                  required
                  autoComplete="email"
                  value={email}
                  onChange={event => setEmail(event.target.value)}
                  placeholder="you@example.com"
                  className={fieldClassName}
                />
              </label>

              <label className="flex flex-col gap-1.5 text-left text-[13px] font-semibold text-[#B9D3D5]">
                <span className="flex items-center justify-between">
                  Password
                  <button
                    type="button"
                    onClick={() => handleSwitchView('forgot')}
                    className="text-[12.5px] font-semibold text-[#5FD6E0] hover:text-[#8CE6EE] hover:underline"
                  >
                    Forgot password?
                  </button>
                </span>
                <span className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    required
                    autoComplete="current-password"
                    value={password}
                    onChange={event => setPassword(event.target.value)}
                    placeholder="Enter your password"
                    className={`${fieldClassName} pr-11`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(current => !current)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-white/55 hover:text-white"
                    aria-label={
                      showPassword ? 'Hide password' : 'Show password'
                    }
                  >
                    {showPassword ? (
                      <IconEyeOff size={18} stroke={1.75} />
                    ) : (
                      <IconEye size={18} stroke={1.75} />
                    )}
                  </button>
                </span>
              </label>

              <button
                type="submit"
                disabled={isSubmitting || isLoading}
                className={ctaClassName}
              >
                {isSubmitting ? 'Signing in…' : 'Sign in'}
              </button>
            </form>

            <p className="text-center text-[13.5px] text-[#B9D3D5]">
              New to Santtai?{' '}
              <button
                type="button"
                onClick={() => handleSwitchView('create')}
                className="font-semibold text-[#5FD6E0] hover:text-[#8CE6EE] hover:underline"
              >
                Create an account
              </button>
            </p>
          </>
        ) : null}

        {view === 'create' ? (
          <>
            <div className="flex flex-col gap-2.5 text-center">
              <span className="text-xs font-bold uppercase tracking-[2px] text-[#5FD6E0]">
                Join the reef
              </span>
              <h1 className="font-dm-serif text-[38px] italic leading-tight text-white">
                Create your account
              </h1>
              <p className="text-[14.5px] leading-[22px] text-[#B9D3D5]">
                Save trips, manage bookings, and dive in faster next time.
              </p>
            </div>

            <form
              className="flex flex-col gap-3.5"
              onSubmit={handleCreateSubmit}
            >
              <label className="flex flex-col gap-1.5 text-left text-[13px] font-semibold text-[#B9D3D5]">
                Full name
                <input
                  type="text"
                  required
                  autoComplete="name"
                  value={fullName}
                  onChange={event => setFullName(event.target.value)}
                  placeholder="Jordan Reyes"
                  className={fieldClassName}
                />
              </label>
              <label className="flex flex-col gap-1.5 text-left text-[13px] font-semibold text-[#B9D3D5]">
                Email
                <input
                  type="email"
                  required
                  autoComplete="email"
                  value={email}
                  onChange={event => setEmail(event.target.value)}
                  placeholder="you@example.com"
                  className={fieldClassName}
                />
              </label>
              <label className="flex flex-col gap-1.5 text-left text-[13px] font-semibold text-[#B9D3D5]">
                Password
                <span className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    required
                    autoComplete="new-password"
                    minLength={8}
                    value={password}
                    onChange={event => setPassword(event.target.value)}
                    placeholder="Create a password"
                    className={`${fieldClassName} pr-11`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(current => !current)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-white/55 hover:text-white"
                    aria-label={
                      showPassword ? 'Hide password' : 'Show password'
                    }
                  >
                    {showPassword ? (
                      <IconEyeOff size={18} stroke={1.75} />
                    ) : (
                      <IconEye size={18} stroke={1.75} />
                    )}
                  </button>
                </span>
              </label>

              <button
                type="submit"
                disabled={isSubmitting || isLoading}
                className={ctaClassName}
              >
                {isSubmitting ? 'Creating account…' : 'Create account'}
              </button>
            </form>

            <p className="text-center text-[13.5px] text-[#B9D3D5]">
              Already have an account?{' '}
              <button
                type="button"
                onClick={() => handleSwitchView('login')}
                className="font-semibold text-[#5FD6E0] hover:text-[#8CE6EE] hover:underline"
              >
                Sign in
              </button>
            </p>
          </>
        ) : null}

        {view === 'forgot' ? (
          <>
            <div className="flex flex-col gap-2.5 text-center">
              <span className="text-xs font-bold uppercase tracking-[2px] text-[#5FD6E0]">
                Reset access
              </span>
              <h1 className="font-dm-serif text-[38px] italic leading-tight text-white">
                Forgot your password?
              </h1>
              <p className="text-[14.5px] leading-[22px] text-[#B9D3D5]">
                Enter your email and we&apos;ll send a reset link.
              </p>
            </div>

            <form
              className="flex flex-col gap-3.5"
              onSubmit={handleForgotSubmit}
            >
              <label className="flex flex-col gap-1.5 text-left text-[13px] font-semibold text-[#B9D3D5]">
                Email
                <input
                  type="email"
                  required
                  autoComplete="email"
                  value={email}
                  onChange={event => setEmail(event.target.value)}
                  placeholder="you@example.com"
                  className={fieldClassName}
                />
              </label>

              <button
                type="submit"
                disabled={isSubmitting || isLoading}
                className={ctaClassName}
              >
                {isSubmitting ? 'Sending…' : 'Send reset link'}
              </button>
            </form>

            <p className="text-center text-[13.5px] text-[#B9D3D5]">
              Remembered it?{' '}
              <button
                type="button"
                onClick={() => handleSwitchView('login')}
                className="font-semibold text-[#5FD6E0] hover:text-[#8CE6EE] hover:underline"
              >
                Back to sign in
              </button>
            </p>
          </>
        ) : null}

        {errorMessage ? (
          <p className="text-center text-sm text-[#FFB4A8]" role="alert">
            {errorMessage}
          </p>
        ) : null}
        {successMessage ? (
          <p className="text-center text-sm text-[#A9E3E9]" role="status">
            {successMessage}
          </p>
        ) : null}

        <p className="text-center text-xs text-white/45">
          You will return to{' '}
          <span className="font-medium text-white/70">
            {redirectAfterLogin}
          </span>{' '}
          after sign in.
        </p>
      </div>
    </div>
  );
};

export default Login;
